import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase, CircularProgress, IconButton } from '@material-ui/core';
import AddressIcon from './AddressIcon';
import AddressResultItem from './AddressResultItem';
import { CustomButton } from '../button';
import { primaryText } from './addressFormat';
import T from './tokens';

const useStyles = makeStyles(() => ({
  root: { width: '100%' },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 36,
    padding: '0 4px 0 10px',
  },
  sectionTitle: { fontSize: 13, letterSpacing: -0.6, color: T.muted },
  textButton: {
    fontSize: 13,
    letterSpacing: -0.6,
    color: T.muted,
    padding: '4px 6px',
    borderRadius: 4,
    '&:hover': { color: T.primaryDark },
  },
  recentRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom: `1px solid ${T.lineSoft}`,
  },
  recentMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    height: 48,
    padding: '0 6px 0 10px',
    textAlign: 'left',
    borderRadius: 0,
    '&:hover': { backgroundColor: T.primarySoft },
  },
  recentIcon: { flex: 'none', color: T.muted, marginRight: 10, display: 'flex' },
  recentText: {
    flex: 1,
    minWidth: 0,
    fontSize: 15,
    letterSpacing: -0.7,
    color: T.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  recentSub: { color: T.muted, fontSize: 13 },
  recentRemove: { width: 32, height: 32, padding: 0, color: T.placeholder, '&:hover': { color: T.text } },
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '56px 0',
    color: T.muted,
    fontSize: 15,
    letterSpacing: -0.7,
    '& svg': { marginBottom: 12, color: T.placeholder },
  },
  stateError: { color: T.primaryDark, '& svg': { color: T.primaryDark } },
  skeletonRow: {
    padding: '14px 12px 14px 42px',
    borderBottom: `1px solid ${T.lineSoft}`,
  },
  bar: {
    height: 12,
    borderRadius: 6,
    backgroundColor: T.lineSoft,
    animation: '$pulse 1.2s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.45 },
  },
  more: {
    padding: '16px 0 4px',
    '& .MuiButton-root': { height: 46, fontSize: 16 },
  },
}));

function Skeleton({ cs }) {
  return (
    <div aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className={cs.skeletonRow}>
          <div className={cs.bar} style={{ width: `${72 - i * 10}%`, marginBottom: 10 }} />
          <div className={cs.bar} style={{ width: `${48 - i * 6}%` }} />
        </div>
      ))}
    </div>
  );
}

function RecentList({ cs, recent, onSelect, onRemove, onClear }) {
  if (!recent.length) return null;
  return (
    <div>
      <div className={cs.sectionHead}>
        <span className={cs.sectionTitle}>최근 선택</span>
        <ButtonBase className={cs.textButton} onClick={onClear}>모두 지우기</ButtonBase>
      </div>
      {recent.map((item) => (
        <div key={item.id} className={cs.recentRow}>
          <ButtonBase className={cs.recentMain} onClick={() => onSelect(item)} focusRipple={false}>
            <span className={cs.recentIcon}><AddressIcon name="clock" size={18} /></span>
            <span className={cs.recentText}>
              {primaryText(item)}
              {item.kind === 'place' && (item.roadAddress || item.jibunAddress) && (
                <span className={cs.recentSub}> · {item.roadAddress || item.jibunAddress}</span>
              )}
            </span>
          </ButtonBase>
          <IconButton className={cs.recentRemove} aria-label="삭제" onClick={() => onRemove(item.id)}>
            <AddressIcon name="close" size={16} />
          </IconButton>
        </div>
      ))}
    </div>
  );
}

// 상태별 본문 — idle(최근 선택) / loading(스켈레톤) / empty / error / ready(결과 + 더 보기)
function AddressResultList({
  status, items, query, hasMore, error, activeIndex = -1, listId,
  onSelect, onLoadMore,
  recent = [], onSelectRecent, onRemoveRecent, onClearRecent,
}) {
  const cs = useStyles();

  if (status === 'idle') {
    return (
      <div className={cs.root}>
        <RecentList cs={cs} recent={recent} onSelect={onSelectRecent} onRemove={onRemoveRecent} onClear={onClearRecent} />
      </div>
    );
  }
  if (status === 'loading' && !items.length) return <div className={cs.root}><Skeleton cs={cs} /></div>;
  if (status === 'empty') {
    return (
      <div className={clsx(cs.root, cs.state)}>
        <AddressIcon name="pin" size={36} />
        검색 결과가 없습니다
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className={clsx(cs.root, cs.state, cs.stateError)}>
        <AddressIcon name="pin" size={36} />
        {error || '주소 검색에 실패했습니다.'}
      </div>
    );
  }

  return (
    <div className={cs.root} role="listbox" id={listId} aria-label="주소 검색 결과">
      {items.map((item, i) => (
        <AddressResultItem
          key={item.id}
          id={`${listId}-${i}`}
          item={item}
          query={query}
          active={i === activeIndex}
          onSelect={onSelect}
        />
      ))}
      {hasMore && (
        <div className={cs.more}>
          <CustomButton fullWidth variant="contained" color="secondary" disabled={status === 'loading'} onClick={onLoadMore}>
            {status === 'loading' ? <CircularProgress size={20} thickness={4} color="inherit" /> : '더 보기'}
          </CustomButton>
        </div>
      )}
    </div>
  );
}

export default AddressResultList;
